import type { Consumer, Subscription } from '@rails/actioncable';
import { useCallback, useEffect, useState } from 'react';

import {
  lastUpdateStore,
  matchTimeStore,
  penaltiesStore,
  renderPenaltyData,
  renderTimings,
  resetTimeStore,
  setTimeStore,
  setTimings,
  timeoutTimeStore,
  updateTimings,
} from '@/features/courts/utils/timings';
import type {
  DodgeballMatch,
  DodgeballMatchPenalty,
} from '@/types/sports/dodgeball';
import { getDeviceId } from '@/utils/device-id';
import { getSubdomain } from '@/utils/get-subdomain';
import { matchData } from '@/utils/match-data';

export interface TimingData {
  matchTime: number;
  resetTime: number;
  setTime: number;
  timeoutTime: number;
}

export interface PenaltyData {
  homeTeam: DodgeballMatchPenalty[];
  awayTeam: DodgeballMatchPenalty[];
}

export const useCourtTimings = (
  id: string | undefined,
  cable: Consumer,
  code?: string,
) => {
  const [channel, setChannel] = useState<Subscription>();
  const [initialized, setInitialized] = useState(false);
  const [penaltyData, setPenaltyData] = useState<PenaltyData>();
  const [renderTime, setRenderTime] = useState(performance.now());
  const [selectionRequired, setSelectionRequired] = useState(false);
  const [sendDataTime, setSendDataTime] = useState(performance.now());
  const [timingData, setTimingData] = useState<TimingData>();
  const [updateTime, setUpdateTime] = useState(performance.now());
  const [internalMatchData, internalSetMatchData] = useState<DodgeballMatch>();
  const [currentCourtId, setCurrentCourtId] = useState<string>();
  const [awaitingMatchData, setAwaitingMatchData] = useState<string>();
  const [differentControlDevice, setDifferentControlDevice] = useState(false);

  useEffect(() => {
    const timeout = setInterval(() => setRenderTime(performance.now()), 250);
    return () => clearInterval(timeout);
  }, [setRenderTime]);

  useEffect(() => {
    const timeout = setInterval(() => setUpdateTime(performance.now()), 100);
    return () => clearInterval(timeout);
  }, [setUpdateTime]);

  useEffect(() => {
    if (!initialized || !code) return;

    const timeout = setInterval(() => setSendDataTime(performance.now()), 5000);
    return () => clearInterval(timeout);
  }, [code, initialized, setSendDataTime]);

  useEffect(() => {
    if (!id || !code) {
      return;
    }

    const time = performance.timeOrigin + performance.now();
    lastUpdateStore.set(id, time);
  }, [id, code, renderTime]);

  useEffect(() => {
    if (!id) {
      return;
    }

    renderTimings(id, setTimingData);
    renderPenaltyData(id, setPenaltyData);
  }, [id, renderTime, setPenaltyData, setTimingData]);

  useEffect(() => {
    if (!id) {
      return;
    }

    renderTimings(id, setTimingData);
    renderPenaltyData(id, setPenaltyData);

    try {
      const storedData = matchData.get(id) as DodgeballMatch;

      if (storedData) {
        internalSetMatchData(storedData);
      }
    } catch {
      // noop
    }
  }, [id, renderTime, setPenaltyData, setTimingData]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const rawMatchData = matchData.get(id) as DodgeballMatch | undefined;
    const lastControl = lastUpdateStore.get(id);
    const lastUpdate = performance.timeOrigin + performance.now();

    if (!code && lastControl && lastControl > lastUpdate - 1000) {
      return;
    }

    rawMatchData && updateTimings(id, rawMatchData.state);
  }, [code, id, updateTime, internalMatchData]);

  useEffect(() => {
    if (awaitingMatchData) {
      const deviceId = getDeviceId();

      channel?.perform('next_match', {
        code,
        device_id: deviceId,
        match_id: awaitingMatchData,
      });
    }
  }, [awaitingMatchData, channel, code]);

  useEffect(() => {
    setCurrentCourtId(id);

    if (!id || currentCourtId === id) {
      return;
    }

    // SCO-429: the channel resolves its court through the named tenant, the
    // same way the HTTP twin does off the `Scored-Tournament` header, so the
    // subscription has to carry the subdomain or it is rejected outright.
    // `take_control`, `next_match`, `update` and `finalize_match` all perform
    // on this subscription, so naming the tenant here covers them too.
    // SCO-428: presenting the pairing code here puts this subscription on the
    // control stream, which carries the organizer's match note. Without it the
    // server streams the anonymous payload, which has no `comments` - so a
    // spectator view keeps working and simply never sees the note.
    const newChannel = cable.subscriptions.create(
      {
        channel: 'Scoreboard::CourtChannel',
        court_id: id,
        subdomain: getSubdomain(),
        ...(code && { code }),
      },
      {
        received: (data: any) => {
          if (initialized && !awaitingMatchData) {
            return;
          }

          // Only initialize if we have a code, otherwise we're just a spectator
          if (code) {
            const deviceId = getDeviceId();

            if (data) {
              if (deviceId !== data.active_device_id) {
                setDifferentControlDevice(true);
                return;
              } else {
                setDifferentControlDevice(false);
              }

              // If we're waiting for a specific match, only initialize if it's the correct one
              if (awaitingMatchData && data.id !== awaitingMatchData) {
                return;
              }
            }

            setInitialized(true);
            setAwaitingMatchData(undefined);
          }

          if (!data) {
            setSelectionRequired(true);
            return;
          } else {
            setSelectionRequired(false);
          }

          setTimeout(() => {
            const existingData = matchData.get(id);
            const lastControl = lastUpdateStore.get(id);
            const lastUpdate = performance.timeOrigin + performance.now();

            if (
              existingData &&
              lastControl &&
              lastControl > lastUpdate - 1000
            ) {
              const { state, ...otherData } = data;
              matchData.set(id, { ...existingData, ...otherData });

              return;
            }

            const {
              state: { timings, penalties },
            } = data;

            setTimings(id, { timings, lastUpdate });
            penaltiesStore.home.set(id, penalties.home_team);
            penaltiesStore.away.set(id, penalties.away_team);

            matchData.set(id, data);
          }, 100);
        },
      },
    );

    setChannel((channel) => {
      if (channel) {
        channel.unsubscribe();
      }

      return newChannel;
    });
  }, [
    cable,
    code,
    currentCourtId,
    initialized,
    id,
    awaitingMatchData,
    setAwaitingMatchData,
    setChannel,
    setCurrentCourtId,
  ]);

  // Unsubscribe from the channel when the component unmounts
  useEffect(() => {
    return () => {
      channel?.unsubscribe();
    };
  }, [channel]);

  const sendWebSocketUpdate = useCallback(() => {
    if (!id || !code || !channel || !initialized) {
      return;
    }

    try {
      const deviceId = getDeviceId();
      const storedData = matchData.get(id) as DodgeballMatch;

      if (!storedData) {
        return;
      }

      const data = {
        match_id: storedData.id,
        device_id: deviceId,
        controller_code: code,
        match: Object.assign(storedData.state, {
          timings: {
            match_time: matchTimeStore.get(id),
            reset_time: resetTimeStore.get(id),
            set_time: setTimeStore.get(id),
            timeout_time: timeoutTimeStore.get(id),
          },
          penalties: {
            home_team: penaltiesStore.home.get(id),
            away_team: penaltiesStore.away.get(id),
          },
        }),
      };

      channel.perform('update', data);
    } catch {
      // noop
    }
  }, [channel, code, id, initialized]);

  useEffect(() => {
    sendWebSocketUpdate();
  }, [channel, code, id, sendDataTime, sendWebSocketUpdate]);

  const sendUpdate = (data: Partial<DodgeballMatch['state']> = {}) => {
    try {
      if (!id) {
        return;
      }

      const storedData = (matchData.get(id) || {}) as DodgeballMatch;
      const storedState = storedData.state;

      matchData.set(id, { ...storedData, state: { ...storedState, ...data } });

      sendWebSocketUpdate();
    } catch {
      // noop
    }
  };

  const setMatchData = (data?: DodgeballMatch) => {
    if (!id) {
      return;
    }

    matchData.set(id, data);
  };

  return {
    channel,
    differentControlDevice,
    matchData: internalMatchData,
    penaltyData,
    selectionRequired,
    sendUpdate,
    sendWebSocketUpdate,
    setAwaitingMatchData,
    setInitialized,
    setMatchData,
    timingData,
  };
};
